/* eslint-disable max-len */
import { CrossSellingFileResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/cross_selling/response/CrossSellingFileResponse';
import { CrossSellingUploadFileResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/cross_selling/response/CrossSellingUploadFileResponse';
import {
  BACKOFFICE_CROSS_SELLING_DELETE_FILE_RESPONSE,
  BACKOFFICE_CROSS_SELLING_GET_FILE_PROGRESS_RESPONSE,
  BACKOFFICE_CROSS_SELLING_GET_FILE_RESPONSE,
  BACKOFFICE_CROSS_SELLING_UPLOAD_FILE_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { FETCHING_FILE, ModalStatusType, NO_FILE } from 'components/FileUpload/paramsByState';
import {
  CROSS_SELLING_MODULE_DISPLAYED_TRACKED,
  CrossSellingContextType,
  CROSS_SELLING_NAMESPACE,
  CrossSellingFileInformation,
  CrossSellingFileStatus,
  CrossSellingState,
  DELETE_FILE,
  GET_FILE,
  REPLACE_FILE,
  SAVE_CROSS_SELLING_REFERENCE,
  SaveActionType,
  UPLOAD_FILE,
} from 'domains/crossSelling/CrossSelling.types';
import { SEARCH_STATUS } from 'utils';

// Init state
const initialState: CrossSellingState = {
  fileUploadStatus: {
    fileStatus: FETCHING_FILE,
    correctRows: [],
    errorRows: [],
    modalStatus: NO_FILE,
    processFileProgress: 0,
  },
  fileRows: [],
  fileInformation: { fileName: '' },
  fileInformationForRequest: { fileName: '' },
  searchStatus: undefined,
  crossSellDisplayed: new Map<string, Set<string>>(),
  crossSellOpened: new Map<string, Set<string>>(),
};

// Saga actions
export const uploadFileCrossSellingRequestSaga = createAction<{
  fileBase64: string;
}>(UPLOAD_FILE);
export const uploadFileCrossSellingResponseSaga = createAction(BACKOFFICE_CROSS_SELLING_UPLOAD_FILE_RESPONSE);
export const replaceFileCrossSellingRequestSaga = createAction(REPLACE_FILE);
export const fetchFileCrossSellingRequestSaga = createAction(GET_FILE);
export const fetchFileCrossSellingResponseSaga = createAction(BACKOFFICE_CROSS_SELLING_GET_FILE_RESPONSE);
export const deleteFileCrossSellingRequestSaga = createAction(DELETE_FILE);
export const deleteFileCrossSellingResponseSaga = createAction(BACKOFFICE_CROSS_SELLING_DELETE_FILE_RESPONSE);
export const fetchCrossSellingFileProcessingResponse = createAction(
  BACKOFFICE_CROSS_SELLING_GET_FILE_PROGRESS_RESPONSE,
);
export const saveCrossSellingReferenceSagaRequest = createAction<{
  mainReferenceNumber: string;
  crossSellReferenceNumber: string;
  actionType: SaveActionType;
  contextType: CrossSellingContextType;
}>(SAVE_CROSS_SELLING_REFERENCE);
export const crossSellingModuleDisplayedTracked = createAction<{
  mainReferenceNumber: string;
  mainReferenceFamilyCode?: string;
  vin?: string;
  mainReferenceSegmentMarketing?: string;
  contextType: CrossSellingContextType;
  location: 'product' | 'catalog';
  numberOfItemsInModule: number;
}>(CROSS_SELLING_MODULE_DISPLAYED_TRACKED);

const buildCrossSellDisplayedKey = (
  crossSellRef: string,
  contextType: CrossSellingContextType,
  location?: 'product' | 'catalog',
) => {
  return location ? `${crossSellRef}::${contextType}::${location}` : `${crossSellRef}::${contextType}`;
};

// Slice
const slice = createSlice({
  name: CROSS_SELLING_NAMESPACE,
  initialState,
  reducers: {
    setCrossSellingFileModalStatus: (state, { payload }: PayloadAction<ModalStatusType>) => {
      state.fileUploadStatus.modalStatus = payload;
    },
    setCrossSellingFileUploadStatus: (state, { payload }: PayloadAction<CrossSellingFileStatus>) => {
      state.fileUploadStatus.fileStatus = payload;
    },
    setCrossSellingFileUploadResponse: (state, { payload }: PayloadAction<CrossSellingUploadFileResponse>) => {
      state.fileUploadStatus.correctRows = payload.correctRows;
      state.fileUploadStatus.errorRows = payload.errorRows;
    },
    setCrossSellingFileInformationForRequest: (state, { payload }: PayloadAction<CrossSellingFileInformation>) => {
      state.fileInformationForRequest = payload;
    },
    setCrossSellingFileResponse: (state, { payload }: PayloadAction<CrossSellingFileResponse>) => {
      const { fileName, fileRows } = payload;
      state.fileInformation = {
        fileName,
      };
      state.fileRows = fileRows;
    },
    setCrossSellingSearchFileStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.searchStatus = payload;
    },
    setDeleteErrorRows: (state) => {
      state.fileUploadStatus.errorRows = [];
    },
    setFileProcessPercent: (state, { payload }: PayloadAction<number>) => {
      state.fileUploadStatus.processFileProgress = payload;
    },
    addCrossSellDisplayed: (
      state,
      {
        payload,
      }: PayloadAction<{
        userId: string;
        crossSellRef: string;
        contextType: CrossSellingContextType;
        location?: 'product' | 'catalog';
      }>,
    ) => {
      const { userId, crossSellRef, contextType, location } = payload;
      const key = buildCrossSellDisplayedKey(crossSellRef, contextType, location);
      const userSet = (state.crossSellDisplayed.get(userId) ?? new Set<string>()).add(key);
      state.crossSellDisplayed.set(userId, userSet);
    },
    addCrossSellOpened: (
      state,
      { payload }: PayloadAction<{ userId: string; crossSellRef: string; contextType: CrossSellingContextType }>,
    ) => {
      const { userId, crossSellRef, contextType } = payload;
      const key = `${crossSellRef}::${contextType}`;
      const userSet = (state.crossSellOpened.get(userId) ?? new Set<string>()).add(key);
      state.crossSellOpened.set(userId, userSet);
    },
  },
});

export const {
  setCrossSellingFileModalStatus,
  setCrossSellingFileUploadResponse,
  setCrossSellingFileInformationForRequest,
  setCrossSellingFileUploadStatus,
  setCrossSellingFileResponse,
  setCrossSellingSearchFileStatus,
  setDeleteErrorRows,
  setFileProcessPercent,
  addCrossSellDisplayed,
  addCrossSellOpened,
} = slice.actions;

export const getCrossSellingFileModalStatus = createSelector(
  (state: RootState) => state.crossSelling.fileUploadStatus.modalStatus,
  (modalStatus) => modalStatus,
);
export const getCrossSellingFileUploadStatus = createSelector(
  (state: RootState) => state.crossSelling.fileUploadStatus.fileStatus,
  (fileStatus) => fileStatus,
);

export const getCrossSellingFileInformationForRequest = createSelector(
  (state: RootState) => state.crossSelling.fileInformationForRequest,
  (fileInformation) => fileInformation,
);

export const getCrossSellingFileInformation = createSelector(
  (state: RootState) => state.crossSelling.fileInformation,
  (fileInformation) => fileInformation,
);

export const getCrossSellingFileErrorRows = createSelector(
  (state: RootState) => state.crossSelling.fileUploadStatus.errorRows,
  (errorRows) => errorRows,
);

export const getCrossSellingFileCorrectRows = createSelector(
  (state: RootState) => state.crossSelling.fileUploadStatus.correctRows,
  (correctRows) => correctRows,
);

export const getCrossSellingFileUploadedRows = createSelector(
  (state: RootState) => state.crossSelling.fileRows,
  (fileRows) => fileRows,
);

export const getCrossSellingFileFetchStatus = createSelector(
  (state: RootState) => state.crossSelling.searchStatus,
  (searchStatus) => searchStatus,
);

export const getProcessFileProgress = createSelector(
  (state: RootState) => state.crossSelling.fileUploadStatus.processFileProgress,
  (processFileProgress) => processFileProgress,
);

export const getCrossSellDisplayed = createSelector(
  (state: RootState) => state.crossSelling.crossSellDisplayed,
  (_state: RootState, userId: string) => userId,
  (crossSellDisplayed, userId) => crossSellDisplayed.get(userId) ?? new Set<string>(),
);

export const getCrossSellOpened = createSelector(
  (state: RootState) => state.crossSelling.crossSellOpened,
  (_state: RootState, userId: string) => userId,
  (crossSellOpened, userId) => crossSellOpened.get(userId) ?? new Set<string>(),
);

export default slice.reducer;
